<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('set-new-password') }}</h4>
    <div class="d-flex card-body justify-content-center">
      <Form
        @submit="onSubmit"
        :validation-schema="schema"
        :initial-values="initialValues"
        v-slot="{ errors, isSubmitting }"
      >
        <!-- Pole na token. Ukrywamy, gdy token przyszedł w URL (link z e-maila). -->
        <Field name="token" type="hidden" v-if="tokenFromUrl" />
        <div class="form-group row mb-2" v-else>
          <label class="col-sm-4 col-form-label">{{ t('token') }}</label>
          <div class="col-sm-8">
            <Field
              name="token"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.token }"
            />
            <div class="invalid-feedback">{{ errors.token }}</div>
          </div>
        </div>

        <!-- Nowe hasło -->
        <div class="form-group row mb-2">
          <label class="col-sm-4 col-form-label">{{ t('new-password') }}</label>
          <div class="col-sm-8">
            <Field
              name="newPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': errors.newPassword }"
            />
            <div class="invalid-feedback">{{ errors.newPassword }}</div>
          </div>
        </div>

        <!-- Powtórzenie nowego hasła -->
        <div class="form-group row mb-3">
          <label class="col-sm-4 col-form-label">{{ t('confirm-password') }}</label>
          <div class="col-sm-8">
            <Field
              name="confirmPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': errors.confirmPassword }"
            />
            <div class="invalid-feedback">{{ errors.confirmPassword }}</div>
          </div>
        </div>

        <!-- Przycisk i komunikat statusu -->
        <div class="form-group d-flex flex-column align-items-center">
          <div class="col-auto">
            <button
              class="btn btn-outline-secondary mt-2"
              :disabled="isSubmitting"
            >
              <span
                v-show="isSubmitting"
                class="spinner-border spinner-border-sm mr-1"
              ></span>
              {{ t('change-password') }}
            </button>
          </div>
          <div class="form-status mt-3" aria-live="polite">
            <div
              v-if="statusMessage && !isStatusDismissed"
              class="alert alert-dismissible fade show"
              :class="success ? 'alert-success' : 'alert-danger'"
              role="alert"
            >
              <span>{{ statusMessage }}</span>
              <button
                type="button"
                class="btn-close"
                aria-label="Zamknij komunikat"
                @click="dismissStatus"
              >
              </button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup>
// Importy potrzebnych bibliotek i narzędzi
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { Form, Field } from "vee-validate";
import * as yup from "yup";
import { useAuthStore } from "@/stores/auth.store";
import { useRoute } from "vue-router";
import router from "@/router";
import { useI18n } from 'vue-i18n';

const route = useRoute();

// Token resetujący przekazywany w query stringu linku z wiadomości e-mail.
// Identyfikacja użytkownika odbywa się wyłącznie po stronie BE na podstawie tokenu —
// adresu email celowo nie ma w URL (PII w logach/historii/refererze).
// Gdy token obecny — pole tokena jest ukrywane i pre-wypełniane.
const tokenFromUrl = computed(() => route.query.token || "");
const initialValues = computed(() => ({ token: tokenFromUrl.value }));

// Defense-in-depth: zapobiegaj wyciekowi tokenu w nagłówku Referer, gdyby z tego widoku
// kliknięto cokolwiek na zewnątrz. Tag wstawiamy tylko na czas życia komponentu.
const REFERRER_META_ID = "reset-password-no-referrer";
onMounted(() => {
  if (document.getElementById(REFERRER_META_ID)) return;
  const meta = document.createElement("meta");
  meta.id = REFERRER_META_ID;
  meta.name = "referrer";
  meta.content = "no-referrer";
  document.head.appendChild(meta);
});
onBeforeUnmount(() => {
  document.getElementById(REFERRER_META_ID)?.remove();
});

// Reaktywne zmienne na sukces/błąd
const success = ref(false);
const error = ref(null);
const isStatusDismissed = ref(false);
const { t } = useI18n();
const statusMessage = computed(() =>
  success.value ? t('password-changed') : error.value || ""
);

const dismissStatus = () => {
  isStatusDismissed.value = true;
};

// Walidacja formularza
const schema = yup.object({
  token: yup.string().required(t('token-required')),
  newPassword: yup
    .string()
    .required(t('user-password-required'))
    .min(6, t('password-must-be-at-least-6')),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], t('passwords-must-be-the-same'))
    .required(t('password-confirmation-required')),
});

// Logika wysyłki formularza
const onSubmit = async (values) => {
  error.value = null;
  success.value = false;
  isStatusDismissed.value = false;

  try {
    const authStore = useAuthStore();

    // Wywołanie akcji zmiany hasła z przekazaniem nowego hasła i tokena.
    // Email nie jest przekazywany — backend wyciąga usera z tokenu.
    const response = await authStore.setPassword(
      values.newPassword,
      values.token
    );

    // Jeśli odpowiedź zawiera błąd, pokazujemy go użytkownikowi
    if(response !== null) {
      error.value = response.split(":").slice(1).join(":").trim();
      return;
    }

    // Sukces – przekierowanie na stronę logowania
    success.value = true;
    router.push("/login");
  } catch (err) {
    error.value = err.message;
    console.error("Błąd HTTP:", err);
  }
};
</script>

<style scoped>
.card-body {
  background: #f5f4f3;
}
.card-body form {
  width: 560px;
}
.col-form-label {
  text-align: right;
  line-height: 25px;
}
.form-group {
  font-size: 12px;
}
button:not(.btn-close) {
  border: 1px solid grey;
  color: #231f20;
  font-size: 15px;
  font-weight: bold;
}
a {
  color: #231f20;
  font-size: 12px;
  text-decoration: none;
}
input {
  border: 0;
  border-left: 1px solid #c8c7c7;
  border-top: 1px solid #c8c7c7;
  padding: 10px;
}
input:focus {
  border-color: #c8c7c7;
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
.form-status {
  min-height: 56px;
  width: 100%;
}
.form-status .alert {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}
.form-status .alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}
</style>
